import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, forkJoin, Subscription } from "rxjs";
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  tap,
} from "rxjs/operators";
import { OrganizationService } from "./organization.service";
import { ThemePreferenceService } from "./theme-preference.service";
import {
  OrgAccessRoleDefinition,
  normalizeAccessRoles,
  roleAllowsFeature,
} from "src/app/shared/org-access-roles";
import { hasBusinessOrganizationAccess } from "src/app/shared/utils/plan.util";

export type AccountUsageMode = "public" | "organization";

export interface OrgBranding {
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  surfaceColor?: string;
  darkPrimaryColor?: string;
  darkAccentColor?: string;
}

export interface OrgContentScope {
  organizationId?: string | null;
  applicationId?: string | null;
  teamId?: string | null;
}

export interface OrgAccountOption {
  organizationId: string;
  organizationName: string;
}

export interface OrgContextState {
  email?: string;
  memberships: any[];
  activeOrganization?: any;
  applications: any[];
  teams: any[];
  activeApplicationId?: string | null;
  activeTeamId?: string | null;
  terminology?: any;
  branding?: OrgBranding | null;
  accountUsageMode: AccountUsageMode;
  /** Always true — Public vs Organization account type is a user choice. */
  allowPublicAccountType?: boolean;
  loaded: boolean;
}

/** Default Flowchest blue used when no org branding is set. */
export const FLOWCHEST_BRAND_BLUE = "#5EA1D8";

const ACCOUNT_MODE_KEY = "accountUsageMode";
/** Per-user account-type choice ("public" or an organizationId); survives logout. */
const ACCOUNT_SELECTION_KEY_PREFIX = "accountUsageSelection:";

@Injectable({ providedIn: "root" })
export class OrgContextService {
  private readonly state$ = new BehaviorSubject<OrgContextState>({
    memberships: [],
    applications: [],
    teams: [],
    branding: null,
    accountUsageMode: this.readStoredMode() || "public",
    allowPublicAccountType: true,
    loaded: false,
  });
  private themeSub: Subscription | null = null;
  private pendingDefaultLoad$: Observable<any> | null = null;

  constructor(
    private organizationService: OrganizationService,
    private themePreference: ThemePreferenceService
  ) {
    this.themeSub = this.themePreference.themeChanges$.subscribe(() => {
      this.syncBrandWithTheme();
    });
  }

  get snapshot(): OrgContextState {
    return this.state$.value;
  }

  isPublicMode(): boolean {
    return this.snapshot.accountUsageMode === "public";
  }

  isOrganizationMode(): boolean {
    if (this.snapshot.accountUsageMode !== "organization") {
      return false;
    }
    // Treat as org mode while context is still hydrating so profile tabs
    // do not flicker between public and org layouts (that freezes mat-tab-group).
    return !!(
      this.snapshot.activeOrganization?.organizationId ||
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("activeOrganizationId"))
    );
  }

  /** Public account type is always available; org policy no longer locks it. */
  canUsePublicAccountType(): boolean {
    return true;
  }

  /**
   * Whether the active organization allows promoting/publishing content
   * to the public catalog. Defaults to true when unset.
   */
  allowsPublicContentPublish(): boolean {
    if (!this.isOrganizationMode()) return true;
    const org = this.snapshot.activeOrganization;
    if (!org) return true;
    if (
      org.allowPublicContentPublish === false ||
      org.AllowPublicContentPublish === false
    ) {
      return false;
    }
    if (
      org.allowPublicContentPublish === true ||
      org.AllowPublicContentPublish === true
    ) {
      return true;
    }
    const access = String(
      org.memberAccountAccess || org.MemberAccountAccess || "InternalAndPublic"
    );
    return access.toLowerCase() !== "internalonly";
  }

  /** Membership list from snapshot (camelCase or PascalCase API payloads). */
  private allMemberships(): any[] {
    const snap: any = this.snapshot;
    return snap?.memberships || snap?.Memberships || [];
  }

  /** Distinct orgs the user belongs to (for account-type picker). */
  organizationOptions(): OrgAccountOption[] {
    const seen = new Map<string, string>();
    // Prefer active memberships; if none match (status casing quirks), use all.
    const list =
      this.activeMemberships().length > 0
        ? this.activeMemberships()
        : this.allMemberships();
    for (const m of list) {
      const id = this.membershipOrganizationId(m);
      if (!id) continue;
      const name =
        m.organizationName ||
        m.OrganizationName ||
        id;
      if (!seen.has(id)) seen.set(id, name);
    }
    // Always include the active org so the switcher stays visible while hydrated.
    const active = this.snapshot.activeOrganization;
    const activeId =
      active?.organizationId || active?.OrganizationId || null;
    if (activeId && !seen.has(activeId)) {
      seen.set(
        activeId,
        active?.name || active?.Name || activeId
      );
    }
    return Array.from(seen.entries()).map(([organizationId, organizationName]) => ({
      organizationId,
      organizationName,
    }));
  }

  get hasOrganization(): boolean {
    return this.isOrganizationMode();
  }

  /** Org-scoped content must go through submit → approve → publish. */
  requiresContentApproval(): boolean {
    return this.hasOrganization;
  }

  canApproveContent(
    organizationId?: string | null,
    applicationId?: string | null,
    teamId?: string | null
  ): boolean {
    if (this.isPublicMode() && !organizationId) return false;
    const orgId =
      organizationId ||
      this.snapshot.activeOrganization?.organizationId ||
      this.snapshot.activeOrganization?.OrganizationId ||
      null;
    if (!orgId) return false;
    const memberships = this.snapshot.memberships || [];
    const accessRoles = this.accessRoles();
    return memberships.some((m: any) => {
      if (this.membershipOrganizationId(m) !== orgId) return false;
      if (
        m.status &&
        String(m.status).toLowerCase() !== "active"
      ) {
        return false;
      }
      const role = this.membershipRole(m);
      if (!roleAllowsFeature(accessRoles, role, "approveContent")) return false;
      return this.membershipCoversScope(
        m,
        applicationId,
        teamId
      );
    });
  }

  /**
   * Membership scope match: org-wide covers all; app/team bindings only
   * cover matching targets (not org-level targets).
   */
  private membershipCoversScope(
    m: any,
    targetApplicationId?: string | null,
    targetTeamId?: string | null
  ): boolean {
    const mApp = this.nullIfNone(m.applicationId || m.ApplicationId);
    const mTeam = this.nullIfNone(m.teamId || m.TeamId);
    const tApp = this.nullIfNone(targetApplicationId);
    const tTeam = this.nullIfNone(targetTeamId);

    if (!mApp && !mTeam) return true;
    if (!tApp && !tTeam) return false;
    if (mTeam) return !!tTeam && mTeam === tTeam;
    if (mApp) return !!tApp && mApp === tApp;
    return false;
  }

  private nullIfNone(value?: string | null): string | null {
    if (!value || String(value).trim().toLowerCase() === "none") return null;
    return String(value).trim();
  }

  private accessRolesCache: OrgAccessRoleDefinition[] | null = null;
  private accessRolesTerminologyRef: any = undefined;

  private accessRoles(): OrgAccessRoleDefinition[] {
    const terminology = this.snapshot.terminology;
    if (
      this.accessRolesCache &&
      this.accessRolesTerminologyRef === terminology
    ) {
      return this.accessRolesCache;
    }
    this.accessRolesTerminologyRef = terminology;
    this.accessRolesCache = normalizeAccessRoles(terminology?.accessRoles);
    return this.accessRolesCache;
  }

  private activeMemberships(): any[] {
    return this.allMemberships().filter((m: any) => {
      const status = m?.status ?? m?.Status;
      return !status || String(status).toLowerCase() === "active";
    });
  }

  /** Normalize context API memberships (supports camelCase / PascalCase). */
  private membershipsFromContext(ctx: any): any[] {
    return ctx?.memberships || ctx?.Memberships || [];
  }

  private membershipOrganizationId(m: any): string | null {
    return m?.organizationId || m?.OrganizationId || null;
  }

  private membershipRole(m: any): string {
    return String(m?.role || m?.Role || "");
  }

  private activeOrganizationId(): string | null {
    return (
      this.snapshot.activeOrganization?.organizationId ||
      this.snapshot.activeOrganization?.OrganizationId ||
      (typeof localStorage !== "undefined"
        ? localStorage.getItem("activeOrganizationId")
        : null)
    );
  }

  /** Memberships for the currently active organization only. */
  private membershipsForActiveOrg(): any[] {
    const orgId = this.activeOrganizationId();
    if (!orgId) return [];
    return this.activeMemberships().filter(
      (m: any) => this.membershipOrganizationId(m) === orgId
    );
  }

  /** True when user has Active memberships but none grant create (Worker-only). */
  isOrgEndUser(): boolean {
    if (!this.isOrganizationMode()) return false;
    const active = this.membershipsForActiveOrg();
    if (!active.length) return false;
    const roles = this.accessRoles();
    return !active.some((m: any) =>
      roleAllowsFeature(roles, this.membershipRole(m), "createContent")
    );
  }

  /** True when any Active membership in the active org grants create-content. */
  canCreateOrgContent(): boolean {
    if (!this.isOrganizationMode()) return false;
    const roles = this.accessRoles();
    return this.membershipsForActiveOrg().some((m: any) =>
      roleAllowsFeature(roles, this.membershipRole(m), "createContent")
    );
  }

  /**
   * Creator chrome (Create, Dashboard, collab settings) in organization mode.
   * Same as create OR Approver/Reviewer inbox needs.
   * In public mode, callers should use platform permissions instead.
   */
  canAccessCreatorChrome(): boolean {
    if (!this.isOrganizationMode()) return false;
    return this.canCreateOrgContent() || this.canApproveContent();
  }

  /** Any membership with AccessOrgAdmin feature enabled (active org). */
  isOrgAdminUser(): boolean {
    if (!this.isOrganizationMode()) return false;
    const roles = this.accessRoles();
    return this.membershipsForActiveOrg().some((m: any) =>
      roleAllowsFeature(roles, this.membershipRole(m), "accessOrgAdmin")
    );
  }

  /** Any membership with ManageOrganization feature enabled (active org). */
  canManageOrganization(): boolean {
    if (!this.isOrganizationMode()) return false;
    const roles = this.accessRoles();
    return this.membershipsForActiveOrg().some((m: any) =>
      roleAllowsFeature(roles, this.membershipRole(m), "manageOrganization")
    );
  }

  /**
   * Org setup page: editing the active org requires manageOrganization;
   * outside an org, only users with no memberships and Business access may
   * create their first organization. Denied until context has loaded.
   */
  canAccessOrgSetup(user: any): boolean {
    if (!this.snapshot.loaded) return false;
    if (this.isOrganizationMode()) return this.canManageOrganization();
    return (
      this.activeMemberships().length === 0 &&
      hasBusinessOrganizationAccess(user)
    );
  }

  /** Org admin console: accessOrgAdmin in the active org, once loaded. */
  canAccessOrgAdminConsole(): boolean {
    return this.snapshot.loaded && this.isOrgAdminUser();
  }

  /**
   * Users tab on search: members whose assigned role grants manageMembers
   * or accessOrgAdmin in the org feature matrix.
   */
  canViewUsersTab(): boolean {
    if (!this.isOrganizationMode()) return false;
    const roles = this.accessRoles();
    return this.membershipsForActiveOrg().some((m: any) => {
      const role = this.membershipRole(m);
      return (
        roleAllowsFeature(roles, role, "manageMembers") ||
        roleAllowsFeature(roles, role, "inviteMembers") ||
        roleAllowsFeature(roles, role, "accessOrgAdmin")
      );
    });
  }

  /** Display name for an access role key, using the active org's role matrix. */
  roleDisplayName(role: string | null | undefined): string {
    if (!role) return "";
    const key = String(role).trim().toLowerCase();
    const match = this.accessRoles().find(
      (r) => String(r?.key || "").toLowerCase() === key
    );
    return (match?.displayName || String(role)).trim();
  }

  /** Can assign/change member roles somewhere in the active organization. */
  canManageMemberRoles(): boolean {
    if (!this.isOrganizationMode()) return false;
    const roles = this.accessRoles();
    return this.membershipsForActiveOrg().some((m: any) => {
      const role = this.membershipRole(m);
      return (
        roleAllowsFeature(roles, role, "manageMembers") ||
        roleAllowsFeature(roles, role, "inviteMembers")
      );
    });
  }

  /** Content access policy for the active organization (defaults to TeamOnly). */
  contentAccessScope(): string {
    if (!this.isOrganizationMode()) return "TeamOnly";
    const scope =
      this.snapshot.activeOrganization?.contentAccessScope ||
      this.snapshot.activeOrganization?.ContentAccessScope;
    return scope || "TeamOnly";
  }

  /** True when the user has an Active membership in the given organization. */
  isActiveMemberOf(organizationId?: string | null): boolean {
    if (!organizationId) return false;
    return this.activeMemberships().some(
      (m: any) => (m.organizationId || m.OrganizationId) === organizationId
    );
  }

  /** Members whose assigned role grants invite/manage/accessOrgAdmin features. */
  canReviewJoinRequests(organizationId?: string | null): boolean {
    if (!organizationId) return false;
    const activeOrgId =
      this.snapshot.activeOrganization?.organizationId ||
      this.snapshot.activeOrganization?.OrganizationId;
    // Feature matrix is loaded for the active org only.
    if (activeOrgId && activeOrgId !== organizationId) return false;
    const roles = this.accessRoles();
    return this.activeMemberships().some((m: any) => {
      if ((m.organizationId || m.OrganizationId) !== organizationId) return false;
      const role = this.membershipRole(m);
      return (
        roleAllowsFeature(roles, role, "inviteMembers") ||
        roleAllowsFeature(roles, role, "manageMembers") ||
        roleAllowsFeature(roles, role, "accessOrgAdmin")
      );
    });
  }

  /** True when assigned role grants manageApplications (or manage org). */
  canManageApplications(): boolean {
    if (!this.isOrganizationMode()) return false;
    const roles = this.accessRoles();
    return this.membershipsForActiveOrg().some((m: any) => {
      const role = this.membershipRole(m);
      return (
        roleAllowsFeature(roles, role, "manageApplications") ||
        roleAllowsFeature(roles, role, "manageOrganization")
      );
    });
  }

  /** True when assigned role grants manageTeams (or broader manage features). */
  canManageTeams(): boolean {
    if (!this.isOrganizationMode()) return false;
    const roles = this.accessRoles();
    return this.membershipsForActiveOrg().some((m: any) => {
      const role = this.membershipRole(m);
      return (
        roleAllowsFeature(roles, role, "manageTeams") ||
        roleAllowsFeature(roles, role, "manageApplications") ||
        roleAllowsFeature(roles, role, "manageOrganization")
      );
    });
  }

  /**
   * Whether a notification belongs to the current account type:
   * - Organization mode → only notifications for the active org
   * - Public mode → personal notifications, plus org notifications for orgs
   *   the user is not an active member of yet (e.g. OrgInvite, join decision)
   */
  isNotificationVisible(notification: any): boolean {
    const orgId =
      String(
        notification?.OrganizationId || notification?.organizationId || ""
      ).trim() || null;
    const contentType = String(
      notification?.ContentType || notification?.contentType || ""
    );
    const isOrgType = contentType.startsWith("Org");
    // Invites must surface on Public for users who are not in the org yet.
    const isOrgInvite = contentType === "OrgInvite";

    if (this.isOrganizationMode()) {
      if (isOrgInvite) return false;
      if (!orgId) return isOrgType;
      const activeOrgId =
        this.snapshot.activeOrganization?.organizationId ||
        localStorage.getItem("activeOrganizationId");
      return orgId === activeOrgId;
    }
    // Public account type
    if (isOrgInvite) return true;
    if (orgId) return !this.isActiveMemberOf(orgId);
    if (isOrgType) return this.activeMemberships().length === 0;
    return true;
  }

  /** Changes whenever the account type or active org changes (for refetching scoped data). */
  accountScopeKey(): string {
    if (!this.isOrganizationMode()) return "public";
    return `organization:${
      this.snapshot.activeOrganization?.organizationId ||
      localStorage.getItem("activeOrganizationId") ||
      ""
    }`;
  }

  /** Logged in with no Active membership — discover apps/teams to join. */
  isUnassignedOrgUser(): boolean {
    return this.snapshot.loaded && this.activeMemberships().length === 0;
  }

  watch(): Observable<OrgContextState> {
    return this.state$.asObservable();
  }

  /** Fields to stamp onto Guide / Guide Pack create payloads. */
  contentScope(): OrgContentScope {
    if (!this.isOrganizationMode()) {
      return { organizationId: null, applicationId: null, teamId: null };
    }
    const s = this.snapshot;
    const organizationId =
      s.activeOrganization?.organizationId ||
      localStorage.getItem("activeOrganizationId") ||
      null;
    const applicationId =
      s.activeApplicationId ||
      localStorage.getItem("activeApplicationId") ||
      null;
    const teamId =
      s.activeTeamId || localStorage.getItem("activeTeamId") || null;
    return { organizationId, applicationId, teamId };
  }

  /** Account filter for "my content" lists: the selected organization in org mode, otherwise personal content only. */
  accountScopeQuery(): { accountScope: "Organization" | "Personal"; organizationId?: string } {
    const organizationId = this.contentScope().organizationId;
    return organizationId
      ? { accountScope: "Organization", organizationId }
      : { accountScope: "Personal" };
  }

  applyToPayload(payload: any): any {
    if (!payload) return payload;
    const scope = this.contentScope();
    if (scope.organizationId) {
      payload.OrganizationId = scope.organizationId;
      payload.organizationId = scope.organizationId;
    }
    if (scope.applicationId) {
      payload.ApplicationId = scope.applicationId;
      payload.applicationId = scope.applicationId;
    }
    if (scope.teamId) {
      payload.TeamId = scope.teamId;
      payload.teamId = scope.teamId;
    }
    return payload;
  }

  /**
   * Switch to public Flowchest usage: clear active org scope/branding,
   * keep memberships for the account-type picker.
   */
  enterPublicMode(): Observable<OrgContextState> {
    this.persistAccountMode("public");
    this.rememberSelection("public");
    this.clearActiveScopeStorage();
    this.clearBranding();
    this.accessRolesCache = null;
    this.accessRolesTerminologyRef = undefined;

    const s = this.snapshot;
    this.state$.next({
      ...s,
      activeOrganization: null,
      applications: [],
      teams: [],
      activeApplicationId: null,
      activeTeamId: null,
      terminology: null,
      branding: null,
      accountUsageMode: "public",
      allowPublicAccountType: true,
      loaded: true,
    });

    // Refresh memberships without activating an org (mode=public).
    return this.organizationService.getContext(undefined, "public").pipe(
      switchMap((ctx) => {
        this.state$.next({
          email: ctx?.email ?? ctx?.Email ?? this.snapshot.email,
          memberships:
            this.membershipsFromContext(ctx).length > 0
              ? this.membershipsFromContext(ctx)
              : this.allMemberships(),
          activeOrganization: null,
          applications: [],
          teams: [],
          activeApplicationId: null,
          activeTeamId: null,
          terminology: null,
          branding: null,
          accountUsageMode: "public",
          allowPublicAccountType: true,
          loaded: true,
        });
        return of(this.snapshot);
      }),
      catchError(() => of(this.snapshot))
    );
  }

  /** Switch to organization usage for the given org. */
  enterOrganizationMode(organizationId: string): Observable<any> {
    if (!organizationId) {
      return this.enterPublicMode();
    }
    this.persistAccountMode("organization");
    this.rememberSelection(organizationId);
    try {
      localStorage.setItem("activeOrganizationId", organizationId);
    } catch {
      /* ignore */
    }
    return this.load(organizationId, null, null, "organization");
  }

  /**
   * Load org context. Respects saved accountUsageMode when modeOverride is omitted:
   * - public → memberships only, no active org
   * - organization → preferred / saved org
   * - no saved mode (e.g. right after login) → the user's remembered account
   *   type if still valid, else organization when memberships exist (Frontline default)
   *
   * Concurrent calls without arguments share one request so callers (shell,
   * login, home) all observe the same resolved account type.
   */
  load(
    organizationId?: string,
    preferredApplicationId?: string | null,
    preferredTeamId?: string | null,
    modeOverride?: AccountUsageMode
  ): Observable<any> {
    if (
      organizationId ||
      preferredApplicationId ||
      preferredTeamId ||
      modeOverride
    ) {
      return this.resolveLoad(
        organizationId,
        preferredApplicationId,
        preferredTeamId,
        modeOverride
      );
    }
    if (!this.pendingDefaultLoad$) {
      this.pendingDefaultLoad$ = this.resolveLoad().pipe(
        finalize(() => {
          this.pendingDefaultLoad$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.pendingDefaultLoad$;
  }

  private resolveLoad(
    organizationId?: string,
    preferredApplicationId?: string | null,
    preferredTeamId?: string | null,
    modeOverride?: AccountUsageMode
  ): Observable<any> {
    const savedMode = this.readStoredMode();

    // Resolve mode before fetching when we already know it.
    if (modeOverride === "public" || (!modeOverride && savedMode === "public")) {
      return this.loadPublicMembershipsOnly();
    }

    if (
      modeOverride === "organization" ||
      savedMode === "organization" ||
      organizationId ||
      localStorage.getItem("activeOrganizationId")
    ) {
      const orgId =
        organizationId ||
        localStorage.getItem("activeOrganizationId") ||
        undefined;
      return this.loadOrgScoped(
        orgId,
        preferredApplicationId,
        preferredTeamId
      );
    }

    // No session mode: fetch memberships, then apply the user's remembered
    // account type, defaulting to org when memberships exist.
    return this.organizationService.getContext(undefined, "public").pipe(
      switchMap((ctx) => {
        const memberships = this.membershipsFromContext(ctx);
        const list = memberships.filter((m: any) => {
          const status = m?.status ?? m?.Status;
          return !status || String(status).toLowerCase() === "active";
        });
        const remembered = this.readRememberedSelection(
          ctx?.email || ctx?.Email
        );
        const wantsPublic = remembered === "public";
        if (list.length > 0 && !wantsPublic) {
          const rememberedOrg =
            remembered &&
            remembered !== "public" &&
            list.some(
              (m: any) => this.membershipOrganizationId(m) === remembered
            )
              ? remembered
              : null;
          const orgId =
            rememberedOrg || this.membershipOrganizationId(list[0]);
          if (orgId) {
            return this.enterOrganizationMode(orgId);
          }
        }
        this.clearBranding();
        this.clearActiveScopeStorage();
        this.persistAccountMode("public");
        this.state$.next({
          email: ctx?.email || ctx?.Email,
          memberships,
          activeOrganization: null,
          applications: [],
          teams: [],
          activeApplicationId: null,
          activeTeamId: null,
          terminology: null,
          branding: null,
          accountUsageMode: "public",
          allowPublicAccountType: true,
          loaded: true,
        });
        return of(ctx);
      }),
      catchError(() => {
        this.clearBranding();
        // Keep any memberships already loaded so the account switcher stays usable.
        this.state$.next({
          ...this.snapshot,
          applications: [],
          teams: [],
          branding: null,
          accountUsageMode: "public",
          allowPublicAccountType: true,
          loaded: true,
        });
        return of(null);
      })
    );
  }

  private loadPublicMembershipsOnly(): Observable<any> {
    this.persistAccountMode("public");
    return this.organizationService.getContext(undefined, "public").pipe(
      switchMap((ctx) => {
        const memberships = this.membershipsFromContext(ctx);
        this.clearBranding();
        this.clearActiveScopeStorage();
        this.state$.next({
          email: ctx?.email || ctx?.Email,
          memberships:
            memberships.length > 0 ? memberships : this.allMemberships(),
          activeOrganization: null,
          applications: [],
          teams: [],
          activeApplicationId: null,
          activeTeamId: null,
          terminology: null,
          branding: null,
          accountUsageMode: "public",
          allowPublicAccountType: true,
          loaded: true,
        });
        return of(ctx);
      }),
      catchError(() => {
        this.clearBranding();
        this.state$.next({
          ...this.snapshot,
          activeOrganization: null,
          applications: [],
          teams: [],
          branding: null,
          accountUsageMode: "public",
          allowPublicAccountType: true,
          loaded: true,
        });
        return of(null);
      })
    );
  }

  private loadOrgScoped(
    organizationId?: string,
    preferredApplicationId?: string | null,
    preferredTeamId?: string | null
  ): Observable<any> {
    return this.organizationService.getContext(organizationId).pipe(
      switchMap((ctx) => {
        const branding = ctx?.branding || ctx?.Branding || null;
        this.applyBranding(branding);
        const activeOrg =
          ctx?.activeOrganization || ctx?.ActiveOrganization || null;
        const orgId =
          activeOrg?.organizationId ||
          activeOrg?.OrganizationId ||
          organizationId ||
          null;
        const memberships = this.membershipsFromContext(ctx);
        if (!orgId) {
          // No org resolved — fall back to public with memberships retained.
          this.persistAccountMode("public");
          this.clearActiveScopeStorage();
          this.state$.next({
            email: ctx?.email || ctx?.Email,
            memberships,
            activeOrganization: null,
            applications: [],
            teams: [],
            activeApplicationId: null,
            activeTeamId: null,
            terminology: ctx?.terminology || ctx?.Terminology || null,
            branding: null,
            accountUsageMode: "public",
            allowPublicAccountType: true,
            loaded: true,
          });
          this.clearBranding();
          return of(ctx);
        }

        return forkJoin({
          applications: this.organizationService.listApplications(orgId).pipe(
            catchError(() => of([]))
          ),
          teams: this.organizationService.listTeams(orgId).pipe(
            catchError(() => of([]))
          ),
        }).pipe(
          tap(({ applications, teams }) => {
            const orgMemberships = memberships.filter(
              (m: any) => this.membershipOrganizationId(m) === orgId
            );
            const isOrgAdmin = orgMemberships.some(
              (m: any) => this.membershipRole(m) === "OrgAdmin"
            );

            let filteredApps = applications || [];
            let filteredTeams = teams || [];

            if (!isOrgAdmin) {
              const allowedAppIds = new Set(
                orgMemberships
                  .map((m: any) => m.applicationId || m.ApplicationId)
                  .filter((id: string) => !!id && id !== "none")
              );
              const allowedTeamIds = new Set(
                orgMemberships
                  .map((m: any) => m.teamId || m.TeamId)
                  .filter((id: string) => !!id && id !== "none")
              );
              filteredApps = filteredApps.filter((a: any) =>
                allowedAppIds.has(a.applicationId || a.ApplicationId)
              );
              filteredTeams = filteredTeams.filter(
                (t: any) =>
                  allowedAppIds.has(t.applicationId || t.ApplicationId) ||
                  allowedTeamIds.has(t.teamId || t.TeamId)
              );
            }

            const preferredApp = preferredApplicationId || null;
            const preferredTeam = preferredTeamId || null;
            const savedApp =
              localStorage.getItem("activeApplicationId") || null;
            const savedTeam = localStorage.getItem("activeTeamId") || null;

            const appId =
              preferredApp &&
              filteredApps.some(
                (a: any) => (a.applicationId || a.ApplicationId) === preferredApp
              )
                ? preferredApp
                : savedApp &&
                  filteredApps.some(
                    (a: any) => (a.applicationId || a.ApplicationId) === savedApp
                  )
                ? savedApp
                : filteredApps?.[0]?.applicationId ||
                  filteredApps?.[0]?.ApplicationId ||
                  null;

            const teamsForApp = filteredTeams.filter(
              (t: any) =>
                !appId ||
                (t.applicationId || t.ApplicationId) === appId
            );
            const teamId =
              preferredTeam &&
              teamsForApp.some(
                (t: any) => (t.teamId || t.TeamId) === preferredTeam
              )
                ? preferredTeam
                : savedTeam &&
                  teamsForApp.some(
                    (t: any) => (t.teamId || t.TeamId) === savedTeam
                  )
                ? savedTeam
                : teamsForApp[0]?.teamId || teamsForApp[0]?.TeamId || null;

            this.persistAccountMode("organization");
            this.persistScope(orgId, appId, teamId);
            try {
              const orgPlan = activeOrg?.planId || activeOrg?.PlanId;
              if (orgPlan) localStorage.setItem("activeOrganizationPlanId", String(orgPlan));
              else localStorage.removeItem("activeOrganizationPlanId");
            } catch {
              /* ignore */
            }
            this.state$.next({
              email: ctx?.email || ctx?.Email,
              memberships:
                memberships.length > 0 ? memberships : this.allMemberships(),
              activeOrganization: activeOrg,
              applications: filteredApps,
              teams: filteredTeams,
              activeApplicationId: appId,
              activeTeamId: teamId,
              terminology: ctx?.terminology || ctx?.Terminology || null,
              branding,
              accountUsageMode: "organization",
              allowPublicAccountType: true,
              loaded: true,
            });
          }),
          switchMap(() => of(ctx))
        );
      }),
      catchError(() => {
        this.clearBranding();
        // Do not wipe memberships on transient failures — keeps Public/Org switcher visible.
        this.state$.next({
          ...this.snapshot,
          loaded: true,
          allowPublicAccountType: true,
          accountUsageMode: this.readStoredMode() || this.snapshot.accountUsageMode,
        });
        return of(null);
      })
    );
  }

  setActiveApplication(applicationId: string | null): void {
    if (!this.isOrganizationMode()) return;
    const s = this.snapshot;
    const teamsForApp = (s.teams || []).filter(
      (t: any) => !applicationId || t.applicationId === applicationId
    );
    const teamId = teamsForApp[0]?.teamId || null;
    this.persistScope(
      s.activeOrganization?.organizationId,
      applicationId,
      teamId
    );
    this.state$.next({
      ...s,
      activeApplicationId: applicationId,
      activeTeamId: teamId,
    });
  }

  setActiveTeam(teamId: string | null): void {
    if (!this.isOrganizationMode()) return;
    const s = this.snapshot;
    this.persistScope(
      s.activeOrganization?.organizationId,
      s.activeApplicationId,
      teamId
    );
    this.state$.next({
      ...s,
      activeTeamId: teamId,
    });
  }

  clear(): void {
    this.pendingDefaultLoad$ = null;
    this.clearBranding();
    this.accessRolesCache = null;
    this.accessRolesTerminologyRef = undefined;
    try {
      localStorage.removeItem("activeOrganizationId");
      localStorage.removeItem("activeOrganizationPlanId");
      localStorage.removeItem("activeApplicationId");
      localStorage.removeItem("activeTeamId");
      localStorage.removeItem(ACCOUNT_MODE_KEY);
    } catch {
      /* ignore */
    }
    this.state$.next({
      memberships: [],
      applications: [],
      teams: [],
      branding: null,
      accountUsageMode: "public",
      loaded: false,
    });
  }

  applyBranding(branding: OrgBranding | null | undefined): void {
    const root = document.documentElement;
    if (!branding) {
      this.clearBranding();
      return;
    }

    const primary = this.normalizeHex(branding.primaryColor);
    const accent = this.normalizeHex(branding.accentColor);
    const surface = this.normalizeHex(branding.surfaceColor);
    const darkPrimary = this.normalizeHex(branding.darkPrimaryColor);
    const darkAccent = this.normalizeHex(branding.darkAccentColor);

    root.classList.add("org-branded");
    if (primary) root.style.setProperty("--org-primary", primary);
    else root.style.removeProperty("--org-primary");
    if (accent) root.style.setProperty("--org-accent", accent);
    else root.style.removeProperty("--org-accent");
    if (surface) root.style.setProperty("--org-surface", surface);
    else root.style.removeProperty("--org-surface");
    if (darkPrimary) root.style.setProperty("--org-primary-dark", darkPrimary);
    else root.style.removeProperty("--org-primary-dark");
    if (darkAccent) root.style.setProperty("--org-accent-dark", darkAccent);
    else root.style.removeProperty("--org-accent-dark");

    const nextBranding: OrgBranding = {
      ...branding,
      primaryColor: primary || branding.primaryColor,
      accentColor: accent || branding.accentColor,
      surfaceColor: surface || branding.surfaceColor,
      darkPrimaryColor: darkPrimary || branding.darkPrimaryColor,
      darkAccentColor: darkAccent || branding.darkAccentColor,
    };

    // Avoid re-emitting identical branding (prevents watch → CD storms on profile).
    const prev = this.snapshot.branding;
    const unchanged =
      prev &&
      prev.logoUrl === nextBranding.logoUrl &&
      prev.primaryColor === nextBranding.primaryColor &&
      prev.accentColor === nextBranding.accentColor &&
      prev.surfaceColor === nextBranding.surfaceColor &&
      prev.darkPrimaryColor === nextBranding.darkPrimaryColor &&
      prev.darkAccentColor === nextBranding.darkAccentColor;
    if (!unchanged) {
      const s = this.snapshot;
      this.state$.next({
        ...s,
        branding: nextBranding,
      });
    }

    this.syncBrandWithTheme();
  }

  /** Apply light or dark org hex onto --fc-brand (replaces Flowchest blue). */
  syncBrandWithTheme(): void {
    const root = document.documentElement;
    const branding = this.snapshot.branding;
    const isDark = this.themePreference.currentTheme === "dark";

    if (!branding || !root.classList.contains("org-branded")) {
      root.style.setProperty("--fc-brand", FLOWCHEST_BRAND_BLUE);
      root.style.setProperty("--fc-brand-accent", FLOWCHEST_BRAND_BLUE);
      return;
    }

    const lightPrimary =
      this.normalizeHex(branding.primaryColor) || FLOWCHEST_BRAND_BLUE;
    const lightAccent =
      this.normalizeHex(branding.accentColor) || lightPrimary;
    const darkPrimary =
      this.normalizeHex(branding.darkPrimaryColor) || lightPrimary;
    const darkAccent =
      this.normalizeHex(branding.darkAccentColor) ||
      this.normalizeHex(branding.accentColor) ||
      darkPrimary;

    root.style.setProperty("--fc-brand", isDark ? darkPrimary : lightPrimary);
    root.style.setProperty(
      "--fc-brand-accent",
      isDark ? darkAccent : lightAccent
    );
  }

  normalizeHex(value?: string | null): string | null {
    if (!value || !String(value).trim()) return null;
    let hex = String(value).trim();
    if (!hex.startsWith("#")) hex = `#${hex}`;
    if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)) return null;
    return hex.toUpperCase();
  }

  private readStoredMode(): AccountUsageMode | null {
    try {
      const v = localStorage.getItem(ACCOUNT_MODE_KEY);
      if (v === "public" || v === "organization") return v;
    } catch {
      /* ignore */
    }
    return null;
  }

  private persistAccountMode(mode: AccountUsageMode): void {
    try {
      localStorage.setItem(ACCOUNT_MODE_KEY, mode);
    } catch {
      /* ignore */
    }
  }

  currentUserEmail(fallback?: string | null): string | null {
    let email = fallback || null;
    if (!email) {
      try {
        const raw = localStorage.getItem("LoggedInUser");
        const user = raw && raw !== "null" ? JSON.parse(raw) : null;
        email = user?.emailAddress || user?.EmailAddress || null;
      } catch {
        email = null;
      }
    }
    email = email || this.snapshot.email || null;
    const normalized = email ? String(email).trim().toLowerCase() : "";
    return normalized || null;
  }

  private rememberSelection(selection: string): void {
    const email = this.currentUserEmail();
    if (!email || !selection) return;
    try {
      localStorage.setItem(ACCOUNT_SELECTION_KEY_PREFIX + email, selection);
    } catch {
      /* ignore */
    }
  }

  private readRememberedSelection(email?: string | null): string | null {
    const key = this.currentUserEmail(email);
    if (!key) return null;
    try {
      return localStorage.getItem(ACCOUNT_SELECTION_KEY_PREFIX + key) || null;
    } catch {
      return null;
    }
  }

  private clearActiveScopeStorage(): void {
    try {
      localStorage.removeItem("activeOrganizationId");
      localStorage.removeItem("activeOrganizationPlanId");
      localStorage.removeItem("activeApplicationId");
      localStorage.removeItem("activeTeamId");
    } catch {
      /* ignore */
    }
  }

  private persistScope(
    orgId?: string | null,
    appId?: string | null,
    teamId?: string | null
  ): void {
    try {
      if (orgId) localStorage.setItem("activeOrganizationId", orgId);
      else localStorage.removeItem("activeOrganizationId");
      if (appId) localStorage.setItem("activeApplicationId", appId);
      else localStorage.removeItem("activeApplicationId");
      if (teamId) localStorage.setItem("activeTeamId", teamId);
      else localStorage.removeItem("activeTeamId");
    } catch {
      /* ignore */
    }
  }

  private clearBranding(): void {
    const root = document.documentElement;
    root.classList.remove("org-branded");
    root.style.removeProperty("--org-primary");
    root.style.removeProperty("--org-accent");
    root.style.removeProperty("--org-surface");
    root.style.removeProperty("--org-primary-dark");
    root.style.removeProperty("--org-accent-dark");
    root.style.setProperty("--fc-brand", FLOWCHEST_BRAND_BLUE);
    root.style.setProperty("--fc-brand-accent", FLOWCHEST_BRAND_BLUE);
  }
}
